import { CoachMarkPreferenceValue } from '@@types/Preference';

import { CoachMarkType, HTTP_CLIENT_HEADER, MemberStatus } from '~/constants';
import { MINUTE, SECOND } from '~/constants/date';
import { PreferenceCategory } from '~/constants/member';
import GA from '~/services/GA/GA';
import globalEventService, { EVENT_NAME } from '~/services/globalEventService';
import localStorage from '~/services/localStorage';
import { channelsStore } from '~/stores/ChannelsStore';
import { coachMarkStore } from '~/stores/CoachMarkStore';
import { mainDetailUiState, threadDetailUiState } from '~/stores/DetailUiState';
import { globalSettingStore } from '~/stores/GlobalSettingStore';
import { membersStore } from '~/stores/MembersStore';
import { api } from '~/stores/providers/Api2';
import logging from '~/stores/providers/logging';
import { threadChannelStore } from '~/stores/ThreadChannelStore';
import { uiState } from '~/stores/UiState';
import { keyBy } from '~/utils/fnUtil/fnUtil';
import { isNative } from '~/utils/runEnvironmentUtil';
import axios from 'axios';
import { isBoolean, isEmpty } from 'lodash';
import { runInAction } from 'mobx';
import { contains } from 'ramda';

import Actions from '../Actions';
import { languagesStore } from '../LanguagesStore';
import { slashCommandAppsStore } from '../SlashCommandAppsStore';
import { userStore } from '../UserStore';
import BootstrapTenant from './bootstrap/Tenant';
import BootstrapUser from './bootstrap/User';

const getPopupChannelId = (url) => /\/messenger\/popup\/(\d{19})/.exec(url)[1];

class BootstrapActions {
  bootstrapUser = new BootstrapUser(this.actions);
  bootstrapTenant = new BootstrapTenant();

  constructor(private actions: Actions) {}

  bootstrapMessenger = () => {
    this.callErrorByInterval();
    this.bootstrapUser.initialize((auth, user) => {
      // NIPA:3221814367842634713 예외처리
      // 카이스트: 2707839738704256900 예외처리
      // 오일뱅크: 2841214491680567256 예외처리
      const tenantIds = ['3221814367842634713', '2707839738704256900'];
      const userIds = ['2707931638325281383'];
      if (tenantIds.includes(user.tenantId) || userIds.includes(user.id)) {
        this.callPingByInterval(user.id);
      }
      this.actions.shortcutActions.bindGlobalKeyMap();
      this.actions.gnbActions.fetchGnbTab();
      this.actions.threadChannelActions.fetchThreadChannelsStatus();
      globalSettingStore.loadTenantGeneralSetting();
      globalSettingStore.loadTenantCommonPreviewSetting();
      globalSettingStore.fetchOrganizationSettings(auth.orgId);
      this.actions.organizationChartActions.fetchUserDepartment(user);
      this.actions.nativeActions.sendUserLocaleToElectron();
      this.handleUserPreferences();
      Promise.all([globalSettingStore.fetchSubscription(), this.fetchChannels()]).then(() => {
        const lastChannelId = userStore.lastChannelId || channelsStore.meChannel?.channelId || userStore.id;
        this.actions.channelActions.selectChannel(lastChannelId);
        this.actions.nativeActions.sendUserInfoToElectron(lastChannelId);
      });
    });
    this.bootstrapTenant.initialize((tenant, tenantSetting) => {
      const { domain, name } = tenant;
      this.actions.nativeActions.sendLogin({ domain, name });
      if (!tenantSetting.conversionNoticeFlag) return;
      this.actions.nativeActions.sendEndFreeTrial();
      this.actions.modalActions.openConversionNotice({ expiredAt: tenantSetting.secondExpiredAt });
    });
    this.fetchSettings();
    this.actions.observeActions.observeIsLoadingLogs();
    this.actions.observeActions.observeCurrentChannelId();
    this.actions.observeActions.observeThreadCurrentChannelId();
    this.actions.observeActions.observeCurrentChannelScreenCapturable();
    this.actions.observeActions.observeContentsChannelId();
    this.actions.observeActions.observeThreadContentsChannelId();
    this.actions.observeActions.observeOnLine();
    this.actions.observeActions.observeCoachMark();
    this.actions.observeActions.observeHighlightTarget();
    this.registerOrgChartMessageHandler();
    this.subscribeStatusMessage();
    this.subscribeNativeRequest();
    this.subscribeScreenCapture();
    this.actions.proxyActions.connectWebsocket(this.actions.websocketActions, () => {
      this.actions.memberActions.sendLocalStatus();
    });
    this.actions.proxyActions.connectWebsocket(this.actions.interTenantWebsocketActions, () => {
      this.actions.memberActions.sendLocalStatus(this.actions.interTenantWebsocketActions);
    });
    this.fetchSupportLanguages();
    this.actions.slashCommandAppActions.fetchRegistrableApps();
    this.subscribeNotificationCLickMessage();
    this.readChannelLogWhenFocused();
  };

  bootstrapPopupChannel = () => {
    this.callErrorByInterval();
    const userPromise = this.bootstrapUser.initialize(async (auth, userResponse) => {
      this.actions.shortcutActions.bindGlobalKeyMap();
      globalSettingStore.loadTenantGeneralSetting();
      globalSettingStore.loadTenantCommonPreviewSetting();
      globalSettingStore.fetchOrganizationSettings(auth.orgId);
      this.actions.organizationChartActions.fetchUserDepartment(userResponse.content);
      this.actions.nativeActions.sendUserLocaleToElectron();
      await globalSettingStore.fetchSubscription();
    });
    this.bootstrapTenant.initialize((tenant, tenantSetting) => {
      const { domain, name } = tenant;
      this.actions.nativeActions.sendLogin({ domain, name });
      if (!tenantSetting.conversionNoticeFlag) return;
      this.actions.nativeActions.sendEndFreeTrial();
      this.actions.modalActions.openConversionNotice({ expiredAt: tenantSetting.secondExpiredAt });
    });
    this.fetchSettings();
    this.actions.observeActions.observeIsLoadingLogs();
    this.actions.observeActions.observeCurrentChannelId();
    this.actions.observeActions.observeThreadCurrentChannelId();
    this.actions.observeActions.observeCurrentChannelScreenCapturable();
    this.actions.observeActions.observeContentsChannelId();
    this.actions.observeActions.observeThreadContentsChannelId();
    this.actions.observeActions.observeOnLine();
    this.actions.observeActions.observeCoachMark();
    this.actions.observeActions.observeHighlightTarget();
    this.subscribeScreenCapture();
    this.actions.proxyActions.connectWebsocket(this.actions.websocketActions, () => {
      // https://nhnent.dooray.com/project/posts/2778108625413339119 댓글 참고
      // 상태 우선순위로 인해 새 창은 자동자리비움 상태로 고정
      this.actions.websocketActions.updateStatus(MemberStatus.AutoAway);
    });
    this.actions.proxyActions.connectWebsocket(this.actions.interTenantWebsocketActions, () => {
      // https://nhnent.dooray.com/project/posts/2778108625413339119 댓글 참고
      // 상태 우선순위로 인해 새 창은 자동자리비움 상태로 고정
      this.actions.interTenantWebsocketActions.updateStatus(MemberStatus.AutoAway);
    });
    this.fetchSupportLanguages();
    this.fetchChannels().then(async () => {
      const lastChannelId = getPopupChannelId(window.location.href);
      // 처음에는 UI 상에 반영돼야 하기 때문에 channelsStore 값을 변경
      mainDetailUiState.setCurrentChannelId(lastChannelId);
      await userPromise;
      this.actions.nativeActions.sendUserInfoToElectron(lastChannelId);
    });
    this.actions.slashCommandAppActions.fetchRegistrableApps();
    this.readChannelLogWhenFocused();
  };

  bootstrapIntegration = async () => {
    this.callErrorByInterval();
    this.bootstrapUser.initialize((auth, userResponse) => {
      globalSettingStore.fetchSubscription();
      globalSettingStore.initCustomSettingStore();
      globalSettingStore.loadTenantGeneralSetting();
      globalSettingStore.loadTenantCommonPreviewSetting();
      globalSettingStore.fetchOrganizationSettings(auth.orgId);
      this.actions.organizationChartActions.fetchUserDepartment(userResponse.content);
      this.actions.nativeActions.sendUserLocaleToElectron();
    });
    this.bootstrapTenant.initialize((tenant, tenantSetting) => {
      const { domain, name } = tenant;
      this.actions.nativeActions.sendLogin({ domain, name });
      if (!tenantSetting.conversionNoticeFlag) return;
      this.actions.nativeActions.sendEndFreeTrial();
      this.actions.modalActions.openConversionNotice({ expiredAt: tenantSetting.secondExpiredAt });
    });
    uiState.setIsFetchingUserApps(true);
    try {
      const appList = await api.commandApp.fetchUserIntegrationApps();
      runInAction(() => {
        slashCommandAppsStore.setUserApps(keyBy('id', appList));
        slashCommandAppsStore.setActiveUserAppId(slashCommandAppsStore.userAppList[0]?.id);
      });
    } finally {
      uiState.setIsFetchingUserApps(false);
    }
  };

  private migrateCoachMark = (coachMarkPreferenceValue: CoachMarkPreferenceValue): CoachMarkPreferenceValue => {
    const storageData = {
      [CoachMarkType.Main]: localStorage.get('tutorialMain'),
      [CoachMarkType.InviteChannel]: localStorage.get('tutorialInviteChannel'),
      [CoachMarkType.StatusIcon]: localStorage.get('tutorialStatusIcon'),
    };
    const hasStorageData =
      isBoolean(storageData.main) && isBoolean(storageData.inviteChannel) && isBoolean(storageData.statusIcon);
    // // 기존 설정 값 마이그레이션
    if (isEmpty(coachMarkPreferenceValue) && hasStorageData) {
      this.actions.settingActions.updateCoachMark(storageData);
    }

    if (hasStorageData) {
      localStorage.removeItem('tutorialMain');
      localStorage.removeItem('tutorialInviteChannel');
      localStorage.removeItem('tutorialStatusIcon');
    }

    return coachMarkPreferenceValue ?? storageData;
  };

  private handleUserPreferences = async () => {
    const coachMarkPreferenceValue = globalSettingStore.userPreferences[PreferenceCategory.COACH_MARK];
    const coachMark = this.migrateCoachMark(coachMarkPreferenceValue);
    if (!coachMark.main) {
      !userStore.effectiveFeatures && (await userStore.fetchEffectiveFeatures());
      this.actions.coachMarkActions.startMainCoachMark();
    } else if (!coachMark.statusIcon) {
      this.actions.coachMarkActions.startStatusIconCoachMark();
    }
  };

  private readChannelLogWhenFocused = () => {
    globalEventService.addEventListener(EVENT_NAME.FOCUS, () => {
      GA.writeSlow('focus');
      const channel = uiState.threadOpen
        ? threadChannelStore.getById(threadDetailUiState.contentsChannelId)
        : channelsStore.getById(mainDetailUiState.contentsChannelId);
      channel && this.actions.channelMembersActions.updateChannelReadSeq(channel);
    });
  };

  private registerOrgChartMessageHandler = () => {
    if (isNative()) {
      this.actions.nativeActions.on('mainView:orgChart', this.actions.orgsActions.handleOrgChartRequest);
    } else {
      globalEventService.addEventListener(
        EVENT_NAME.MESSAGE,
        (message: MessageEvent<any>) => {
          if (contains(window.location.host, message.origin)) {
            this.actions.orgsActions.handleOrgChartFromMessage(message);
          }
        },
        false
      );
    }
  };

  private subscribeStatusMessage = () => {
    // 일렉트론에서 자리비움과 같은 상태가 변경된 정보가 넘어올 경우 웹에서 상태변경 요청 처리
    this.actions.nativeActions.onStatusChange((data) => {
      logging.debug('subscribeStatusMessage', data);
      if (data.memberStatus === 'LOGOUT') {
        return this.actions.userActions.logout();
      }
      this.actions.userActions.updateStatusFromNative(data.memberStatus);
    });
  };

  private subscribeScreenCapture = () => {
    this.actions.nativeActions.onScreenCapture(this.actions.uploadActions.uploadScreenCaptureFromNative);
  };

  private subscribeNativeRequest = () => {
    this.actions.nativeActions.onGetStatus(() => {
      const status = membersStore.getStatusById(userStore.id);
      this.actions.nativeActions.sendUpdateStatus(status);
    });
    this.actions.nativeActions.onGetCount(() => {
      const count = channelsStore.unreadCount;
      this.actions.nativeActions.sendDockBadge({
        count: Math.max(0, count),
      });
    });
  };

  private fetchSupportLanguages = () => {
    languagesStore.loadLanguages();
  };

  private fetchChannels = () => {
    return this.actions.proxyActions.fetchAndSetChannels();
  };

  private fetchSettings = () => {
    globalSettingStore.fetchDoorayNewsSetting();
    globalSettingStore.fetchOrganizationChartSetting(({ use }) => {
      if (use) {
        return Promise.all([
          globalSettingStore.fetchOrganizationChartGeneralSetting(),
          this.actions.organizationChartActions.fetchPrimaryDepartments(),
        ]);
      }
      return Promise.resolve();
    });
    globalSettingStore.fetchSystemCommands();
    globalSettingStore.fetchMessengerGeneralSetting();
    globalSettingStore.initCustomSettingStore();
    globalSettingStore.fetchAvailableAiProductItem();
  };

  private subscribeNotificationCLickMessage = () => {
    //Notification Balloon 클릭했을 때 해당 채널로 이동
    this.actions.nativeActions.onShowChannel(this.handleOnShowChannel);
  };

  private handleOnShowChannel = ({ channelId }) => {
    if (coachMarkStore.isInInviteChannelCoachMark) {
      this.actions.coachMarkActions.complete();
    }

    this.actions.channelActions.selectChannelByNoti(channelId);
    // 노티 클릭으로 다른 대화방 선택 시 기존에 열린 레이어를 모두 닫아주도록 body에 클릭 이벤트를 트리거
    this.triggerClickToBody();
  };

  private triggerClickToBody = () => {
    const event = document.createEvent('HTMLEvents');
    event.initEvent('click', true, true);
    document.body.dispatchEvent(event);
  };

  private callPingByInterval = (memberId: string) => {
    window.setInterval(() => {
      if (navigator.onLine) {
        axios.get(`/monitor/ping?t=${performance.now()}`, {
          timeout: SECOND,
          headers: {
            [HTTP_CLIENT_HEADER]: `${memberId}-${performance.now()}`,
          },
        });
      }
    }, MINUTE);
  };

  private callErrorByInterval = () => {
    window.setInterval(() => {
      const tokens = this.actions.errorActions.tokens.slice(0, 100);
      if (tokens.length === 0) {
        return;
      }
      axios.get(`/monitor/report?v=2&ts=${tokens.join(',')}`, { timeout: SECOND }).then(() => {
        if (this.actions.errorActions.tokens.length === tokens.length) {
          this.actions.errorActions.handleSendTokens(tokens);
        }
      });
    }, MINUTE);
  };
}

export default BootstrapActions;
